// File: src/pages/InvoiceEditPage.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BackButton from '../components/BackButton';
import dayjs from 'dayjs';

export default function InvoiceEditPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  // Company settings (if needed)
  const [settings, setSettings] = useState({});

  // Invoice form fields
  const [form, setForm] = useState({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    notes: '',
    discount: '0',
  });

  // Invoice items with Cess support
  const [items, setItems] = useState([{
    Description: '',
    HSN: '',
    Size: '',
    Quantity: '',
    Rate: '',
    GSTPercent: '0',
    Cess: '0',
    CGST: '0',
    SGST: '0',
    CessAmount: '0',
    Amount: '0'
  }]);

  // Customers list for react-select
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Product-search modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearch, setProductSearch]   = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productRowIdx, setProductRowIdx]   = useState(null);

  // Fetch settings, invoice and customers on mount / invoiceId change
  useEffect(() => {
    api.get('/settings/get')
       .then(res => setSettings(res.data || {}))
       .catch(console.error);

    fetchInvoice();
    fetchCustomers();
    // eslint-disable-next-line
  }, [invoiceId]);

  // Load existing invoice data
  const fetchInvoice = async () => {
    try {
      const res = await api.get(`/invoices/${invoiceId}`);
      const { invoice, items: fetchedItems } = res.data;

      // Populate form
      setForm({
        customerId: invoice.CustomerID,
        invoiceNumber: invoice.InvoiceNo,
        invoiceDate: invoice.InvoiceDate?.split('T')[0] || '',
        dueDate: invoice.DueDate ? invoice.DueDate.split('T')[0] : '',
        notes: invoice.Notes || '',
        discount: invoice.Discount?.toString() || '0',
      });

      // Set selected customer for react-select
      setSelectedCustomer({
        value: invoice.CustomerID,
        label: invoice.CustomerName,
      });

      // Map fetched items to include Cess and recalc amounts
      if (fetchedItems && fetchedItems.length) {
        setItems(fetchedItems.map(i => calculateItemAmount({
          Description: i.Description,
          HSN:         i.HSN,
          Size:        i.Size,
          Quantity:    i.Quantity.toString(),
          Rate:        i.Rate.toString(),
          GSTPercent:  i.GSTPercent.toString(),
          Cess:        (i.Cess || 0).toString()
        })));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load invoice');
    }
  };

  // Load customers for selection
  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load customers');
    }
  };

  // Calculate CGST/SGST/Cess/Amount for an item object
  const calculateItemAmount = item => {
    const qty        = parseFloat(item.Quantity)   || 0;
    const rate       = parseFloat(item.Rate)       || 0;
    const gstPct     = parseFloat(item.GSTPercent) || 0;
    const cessPct    = parseFloat(item.Cess)       || 0;

    const baseAmount = qty * rate;
    const cgstAmt    = baseAmount * (gstPct/2) / 100;
    const sgstAmt    = baseAmount * (gstPct/2) / 100;
    const cessAmt    = baseAmount * cessPct / 100;
    const totalAmt   = baseAmount + cgstAmt + sgstAmt + cessAmt;

    return {
      ...item,
      CGST:       cgstAmt.toFixed(2),
      SGST:       sgstAmt.toFixed(2),
      CessAmount: cessAmt.toFixed(2),
      Amount:     totalAmt.toFixed(2)
    };
  };

  // Handle individual item field change
  const handleItemChange = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated.map((itm, i) => (i === idx ? calculateItemAmount(itm) : itm)));
  };

  // Add or remove an item row
  const addItem = () => setItems(prev => ([ ...prev, {
    Description: '', HSN: '', Size: '', Quantity: '', Rate: '',
    GSTPercent: '0', Cess: '0', CGST: '0', SGST: '0',
    CessAmount: '0', Amount: '0'
  } ]));
  const removeItem = idx => setItems(prev => prev.filter((_,i) => i !== idx));

  // Open product search modal for row
  const handleOpenProductModal = idx => {
    setProductRowIdx(idx);
    setShowProductModal(true);
    setProductSearch('');
    setProductResults([]);
  };

  // Fetch matching products
  const fetchProductSearch = async q => {
    setProductSearch(q);
    if (!q) {
      setProductResults([]);
      return;
    }
    try {
      const res = await api.get('/products', { params: { search: q } });
      setProductResults(res.data || []);
    } catch {
      setProductResults([]);
    }
  };

  // Summary totals
  const subTotal   = items.reduce((sum,i) => sum + parseFloat(i.Amount||0), 0);
  const discount   = parseFloat(form.discount || 0);
  const grandTotal = (subTotal - discount > 0 ? subTotal - discount : 0).toFixed(2);

  // Handle form submit
  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await api.put(`/invoices/${invoiceId}`, {
        customerId:    selectedCustomer?.value,
        invoiceNumber: form.invoiceNumber,
        invoiceDate:   form.invoiceDate,
        dueDate:       form.dueDate,
        notes:         form.notes,
        discount:      form.discount,
        items: items.map(i => ({
          Description: i.Description,
          HSN:         i.HSN,
          Size:        i.Size,
          Quantity:    i.Quantity,
          Rate:        i.Rate,
          GSTPercent:  i.GSTPercent,
          Cess:        i.Cess
        }))
      });
      toast.success('Invoice updated successfully');
      setTimeout(() => navigate('/invoices'), 1000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update invoice');
    }
  };

  // Styles
  const cardStyle = {
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '2rem',
    boxShadow: '0 8px 48px rgba(40,110,250,0.10)',
    border: '1.5px solid rgba(180,210,255,0.18)',
    margin: '0 auto',
    maxWidth: 1100,
  };
  const stickyBarStyle = {
    background: 'rgba(255,255,255,0.98)',
    borderRadius: '2rem',
    boxShadow: '0 2px 16px rgba(40,110,250,0.09)',
    border: '1.5px solid #e1e9f7',
    position: 'sticky',
    bottom: 10,
    zIndex: 2,
    maxWidth: 980,
    margin: '0 auto',
  };

  // React-select options
  const customerOptions = customers.map(c => ({
    value: c.CustomerID,
    label: c.Name
  }));

  return (
    <div className="min-vh-100 d-flex flex-column" style={{
      background: 'linear-gradient(120deg, #b6d6fa 0%, #f0f6ff 100%)',
      paddingBottom: 60
    }}>
      <BackButton />

      <div className="container py-4 flex-grow-1 d-flex flex-column align-items-center">
        <h2 className="mb-4 fw-bold text-primary text-center">
          <span role="img" aria-label="edit">✏️</span> Edit Invoice
        </h2>

        <div className="card p-4 shadow-lg rounded-4 w-100 mb-4" style={cardStyle}>
          <form autoComplete="off" onSubmit={handleSubmit}>
            {/* Customer, Dates, Discount, Notes */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Customer</label>
                <Select
                  classNamePrefix="react-select"
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={opt => {
                    setSelectedCustomer(opt);
                    setForm(f => ({ ...f, customerId: opt?.value }));
                  }}
                  placeholder="Select customer..."
                  styles={{
                    control: provided => ({ ...provided, borderRadius: 40 })
                  }}
                  isClearable
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Invoice #</label>
                <input
                  type="text"
                  className="form-control rounded-pill"
                  value={form.invoiceNumber}
                  readOnly
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Invoice Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill"
                  value={form.invoiceDate}
                  onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill"
                  value={form.dueDate}
                  onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount (₹)</label>
                <input
                  type="number"
                  className="form-control rounded-pill"
                  value={form.discount}
                  onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control rounded-3"
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-semibold mb-0">Invoice Items</h5>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addItem}>
                  + Add Item
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>GST %</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>Cess %</th>
                      <th>Cess Amt</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              className="form-control"
                              placeholder="Description"
                              value={item.Description}
                              onChange={e => handleItemChange(idx, 'Description', e.target.value)}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleOpenProductModal(idx)}
                            >
                              🔍
                            </button>
                          </div>
                        </td>
                        <td>
                          <input
                            className="form-control"
                            value={item.HSN}
                            onChange={e => handleItemChange(idx, 'HSN', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control"
                            value={item.Size}
                            onChange={e => handleItemChange(idx, 'Size', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={item.Quantity}
                            onChange={e => handleItemChange(idx, 'Quantity', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={item.Rate}
                            onChange={e => handleItemChange(idx, 'Rate', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={item.GSTPercent}
                            onChange={e => handleItemChange(idx, 'GSTPercent', e.target.value)}
                          />
                        </td>
                        <td>
                          <input className="form-control" value={item.CGST} readOnly />
                        </td>
                        <td>
                          <input className="form-control" value={item.SGST} readOnly />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={item.Cess}
                            onChange={e => handleItemChange(idx, 'Cess', e.target.value)}
                          />
                        </td>
                        <td>
                          <input className="form-control" value={item.CessAmount} readOnly />
                        </td>
                        <td>
                          <input className="form-control" value={item.Amount} readOnly />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeItem(idx)}
                            disabled={items.length === 1}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sticky Summary */}
            <div className="shadow rounded-4 p-3 mb-4 bg-white d-flex justify-content-between flex-wrap" style={stickyBarStyle}>
              <span className="fw-bold">Subtotal: ₹{subTotal.toFixed(2)}</span>
              <span className="fw-bold">Discount: ₹{discount.toFixed(2)}</span>
              <span className="fw-bold">Grand Total: ₹{grandTotal}</span>
            </div>

            <button type="submit" className="btn btn-primary w-100 py-3 rounded-pill fs-5">
              Update Invoice
            </button>
          </form>
        </div>
      </div>

      {/* Product Search Modal */}
      {showProductModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', background: 'rgba(0,0,0,0.18)', zIndex: 2000 }}
          tabIndex={-1}
          onClick={() => setShowProductModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content shadow-lg rounded-3">
              <div className="modal-header">
                <h5 className="modal-title">Search Product</h5>
                <button type="button" className="btn-close" onClick={() => setShowProductModal(false)} />
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Type product name or HSN"
                  value={productSearch}
                  onChange={e => fetchProductSearch(e.target.value)}
                  autoFocus
                />
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {productResults.length === 0 && productSearch && (
                    <div className="text-center text-muted py-2">No products found.</div>
                  )}
                  {productResults.map(prod => (
                    <button
                      key={prod.ProductID}
                      type="button"
                      className="list-group-item list-group-item-action mb-1"
                      onClick={() => {
                        const updated = [...items];
                        updated[productRowIdx] = calculateItemAmount({
                          ...updated[productRowIdx],
                          Description: prod.Name,
                          HSN: prod.HSN,
                          Rate: prod.Rate.toString(),
                          GSTPercent: prod.GSTPercent.toString()
                        });
                        setItems(updated);
                        setShowProductModal(false);
                      }}
                    >
                      <div className="fw-bold">{prod.Name}</div>
                      <small>HSN: {prod.HSN} · Rate: ₹{prod.Rate} · GST: {prod.GSTPercent}%</small>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print { .no-print { display: none !important; } }
      `}</style>
    </div>
  );
}
